<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue';
import {
  FormUploadEmits,
  FormUploadFile,
  FormUploadProps,
  FormUploadRef,
} from '@/types/form/form-upload';
import SiteRichText from '@/elements/SiteRichText.vue';
import utils from '@/utils';
import { ToastProvider } from '@/types/layout';
import { DynamicFormContext, DynamicFormFieldValidateResult } from '@/types/form/dynamic-form';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import FormElMsg from '@/form/other/FormElMsg.vue';
import { uniqBy } from 'lodash-es';

const props = defineProps<FormUploadProps>();
const emit = defineEmits<FormUploadEmits>();
const formContext = inject<DynamicFormContext>('formContext')!;
const toast = inject<ToastProvider>('toast')!;
const rootEl = ref<HTMLElement>();
const files = ref<FormUploadFile[]>([]);
const isRequired = ref(props.item?.fields?.required?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const msg = ref<FormElMsgData>();
const fields = computed(() => props.item?.fields);
const fieldName = computed(() => fields.value?.fieldName?.value || '');
const acceptTypes = computed(() => utils.sp.getVals(fields.value?.acceptTypes ?? []) as string[]);
const multiple = computed(() => fields.value?.multiple.value);
const maxSizeLimit = computed(() => fields.value?.maxSize.value ?? 0);
const totalSize = computed(() => files.value.reduce((acc, file) => acc + file.size, 0));
const isValidAcceptTypes = (file: File) => {
  if (!acceptTypes.value || acceptTypes.value.length === 0) return true;
  const extension = utils.file.getExt(file.name);
  return acceptTypes.value.includes(file.type) || acceptTypes.value.includes(`.${extension}`);
};
const onSelectFile = async () => {
  if (!fields.value) return;
  const [selectedFiles, error] = await utils.file.select({
    acceptTypes: acceptTypes.value,
    multiple: multiple.value,
  });
  if (error) {
    toast.showError(error);
    return;
  }
  if (!selectedFiles || selectedFiles.length === 0) return;
  console.log('selectedFiles', selectedFiles);
  const tempFiles: FormUploadFile[] = [];
  for (let file of selectedFiles) {
    if (!isValidAcceptTypes(file)) {
      toast.showError(
        utils.string.ifEmpty(fields.value.invalidTypeMessage.value, `File type is not allowed`)
      );
      return;
    }
    tempFiles.push({
      size: file.size,
      name: file.name,
      type: file.type,
      tempPath: utils.file.getTempPath(file),
      file: file,
    });
  }
  const summarySize = totalSize.value + tempFiles.reduce((acc, file) => acc + file.size, 0);
  if (maxSizeLimit.value > 0 && summarySize > maxSizeLimit.value) {
    toast.showError(`Total file size exceeds the limit of ${maxSizeLimit.value} bytes`);
    return;
  }
  if (multiple.value) {
    let result = [...files.value, ...tempFiles];
    result = uniqBy(result, (result: FormUploadFile) => result.name);
    files.value = result;
  } else {
    files.value = tempFiles;
  }
  emit('change', 'form-upload', fieldName.value, files.value, files.value);
};
const removeFile = async (index: number) => {
  files.value.splice(index, 1);
};
const setRequired = (required: boolean) => {
  isRequired.value = required;
};

const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};

const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};

const updateValue = (value?: FileList | null) => {
  if (!value) {
    files.value = [];
    return;
  }
  const tempFiles: FormUploadFile[] = [];
  for (let i = 0; i < value.length; i++) {
    tempFiles.push({
      size: value[i].size,
      name: value[i].name,
      type: value[i].type,
      tempPath: utils.file.getTempPath(value[i]),
      file: value[i],
    });
  }
  files.value = tempFiles;
};

const getValue = () => {
  return files.value;
};
const validate = async () => {
  const fileItems: File[] = files.value.map((file) => file.file);
  const value = utils.file.files2FileList(fileItems);
  const result: DynamicFormFieldValidateResult = {
    value,
    valid: true,
    message: '',
  };
  if (!isVisible.value) {
    return result;
  }
  if (isRequired.value && files.value.length === 0) {
    result.valid = false;
    result.message = 'Required field';
    msg.value = {
      type: 'error',
      message: result.message,
    };
    return result;
  }
  msg.value = undefined;
  return result;
};
const getRefData = () => {
  return {
    el: rootEl.value!,
    type: 'form-input',
    name: fieldName.value,
    id: props.item.id,
    visible: isVisible.value,
    setRequired,
    setDisabled,
    setVisible,
    updateValue,
    getValue,
    validate,
  } as FormUploadRef;
};
watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);
onMounted(() => {
  formContext.regFormEl(getRefData());
});
onUnmounted(() => {
  formContext.unRegFormEl(getRefData());
});
defineExpose(getRefData());
</script>

<template>
  <div
    class="f-upload"
    v-if="fields"
    ref="rootEl"
    :class="{ disabled: isDisabled, multiple, 'has-error': msg?.type === 'error' }"
  >
    <div class="f-upload__inner">
      <div v-if="fields.multiple.value" class="f-upload__multiple">
        <site-rich-text :field="fields.multipleTitle" v-if="files.length === 0" />
        <div class="f-upload__files" v-else>
          <div class="f-upload__file" v-for="(file, index) of files" :key="index">
            <span class="f-upload__file-name">{{ file.name }}</span>
            <icon name="close" size="xxs" @click="removeFile(index)" />
          </div>
        </div>
      </div>
      <div class="f-upload__upbox">
        <div class="f-upload__dropper" @click="onSelectFile" v-if="multiple || files.length === 0">
          <div class="f-upload__icon">
            <icon name="upload" size="xs" :field="fields.icon" />
          </div>
          <site-rich-text :field="fields.label" class="f-upload__label" />
        </div>
        <div class="f-upload__img" v-if="!multiple && files.length > 0">
          <div class="f-upload__close" @click="files = []">
            <icon name="close" />
          </div>
          <img :src="files[0].tempPath" :alt="files[0].name" />
        </div>
      </div>
    </div>
    <form-el-msg :msg="msg" />
  </div>
</template>

<style scoped lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';
.f-upload {
  $c: &;
  display: flex;
  flex-direction: column;
  gap: 8px;
  &__inner {
    display: flex;
    gap: 8px;
  }
  &__multiple {
    flex: 1;
    background-color: var(--item-back-color, $white);
    color: var(--item-color, #c4c4c4);
    border: 1px solid var(--item-border-color, #e0e0e0);
    border-radius: 8px;
    padding: 8px 16px;
  }
  &__files {
    display: flex;
    flex-wrap: wrap;
  }
  &__file {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f1f1f1;
    color: #000;
    border-radius: 4px;
    padding: 2px 12px;
  }
  &__upbox {
    flex-shrink: 0;
    display: flex;
    width: 32vw;
    aspect-ratio: 3/4;
    background-color: var(--item-back-color, $white);
    color: var(--item-color, #c4c4c4);
    border: 1px solid var(--item-border-color, #e0e0e0);
    border-radius: 8px;
    user-select: none;
    overflow: hidden;
  }
  &__dropper {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 16px;
    cursor: pointer;
  }
  &__icon {
    width: 44px;
    height: 44px;
    border-radius: 22px;
    background-color: rgba(#f1f1f1, 0.95);
    color: #121e6e;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__label {
    @include h9;
    text-align: center;
  }
  &__img {
    position: relative;
    width: 100%;
    height: 100%;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }
  &__close {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: $red;
    > svg {
      height: 10px;
      color: $white;
    }
  }
  &.multiple {
    #{$c}__upbox {
      border-style: dashed;
    }
  }
  @include tablet-landscape {
    &__upbox {
      flex-shrink: 0;
      width: 150px;
      text-align: center;
    }
  }
}
</style>
